<template>
  <bottom-bar-layout class="page-education-column"
                     :show-bar="joinColumnistUrl !== ''"
                     :shadow="false"
  >
    <div class="page-education-column-banner">
      <ym-banner class="page-education-column-banner-content"
                 :banner="banner"
                 width="690"
                 height="258"
                 :options="bannerOptions"
                 @click="handleClickBanner"
      />
    </div>

    <ym-tabs v-model="tabSelected"
             sticky
             size="medium"
             :border="false"
             :offset-top="tabsOffsetTop"
    >
      <ym-tab v-for="(item, index) in render.tabBar"
              :key="index"
              class="tab"
              :name="item.type"
              :title="item.name"
      >
        <template v-if="item.type === 'activity'">
          <section-block v-if="activity.notices.list.length > 0"
                         class="page-education-column-header-block"
                         title="圆桌活动预告"
                         :show-more-route="noticeActivityMoreRoute"
          >
            <notice-activity-list :list="activity.notices.list"
                                  :hint="activity.notices.clickHint"
            />
          </section-block>
          <section-block v-if="activity.reviews.list.length > 0"
                         title="往期活动精选"
                         :show-more-route="reviewActivityMoreRoute"
          >
            <review-activity-list :list="activity.reviews.list" />
          </section-block>
          <empty-page v-if="showEmptyActivity"
                      class="page-education-column-empty"
                      hint="暂无内容"
          />
        </template>
        <template v-if="item.type === 'columnistArticle'">
          <section-block v-if="columnistArticle.articles.list.length > 0"
                         class="page-education-column-header-block"
                         title="最新专栏作者图文精选"
                         :show-more-route="selectedArticleMoreRoute"
          >
            <selected-article-list :list="columnistArticle.articles.list" />
          </section-block>
          <section-block v-if="columnistArticle.columnists.list.length > 0"
                         title="所有专栏作者"
                         :show-more-route="columnistMoreRoute"
          >
            <columnist-list class="page-education-column-columnist-list"
                            :list="columnistArticle.columnists.list"
            />
          </section-block>
          <empty-page v-if="showEmptyColumnistArticle"
                      class="page-education-column-empty"
                      hint="暂无内容"
          />
        </template>
      </ym-tab>
    </ym-tabs>

    <template #tabbar>
      <div class="page-education-column-join-bar"
           @click="handleClickJoinColumnist"
      >
        申请成为专栏作者
      </div>
    </template>
  </bottom-bar-layout>
</template>

<script>
  import { Tabs, Tab } from '@ymba/ym-ui';
  import BottomBarLayout from '@/layout/BottomBarLayout';
  import Banner from '@/components/Banner';
  import SectionBlock from '@/pages/EducationColumn/components/SectionBlock';
  import NoticeActivityList from '@/pages/EducationColumn/components/NoticeActivityList';
  import ReviewActivityList from '@/pages/EducationColumn/components/ReviewActivityList';
  import SelectedArticleList from '@/pages/EducationColumn/components/SelectedArticleList';
  import ColumnistList from '@/pages/EducationColumn/components/ColumnistList';
  import EmptyPage from '@/components/EmptyPage';
  import { getEducationColumnIndex } from '@/services/education-column';
  import { upperCaseFirst, locationHref } from '@/utils';
  import offsetTop from '@/mixins/dom/offset-top';

  export default {
    name: 'PageEducationColumn',
    mixins: [offsetTop],
    metaInfo() {
      return {
        title: this.render.pageTitle,
      };
    },
    components: {
      BottomBarLayout,
      'ym-banner': Banner,
      'ym-tabs': Tabs,
      'ym-tab': Tab,
      SectionBlock,
      NoticeActivityList,
      ReviewActivityList,
      SelectedArticleList,
      ColumnistList,
      EmptyPage,
    },
    data() {
      return {
        loaded: false,
        tabSelected: 'activity',

        banner: [],
        bannerOptions: {
          slidesPerView: 1,
          spaceBetween: 0,
        },
        activity: {
          notices: {
            list: [],
            showMore: false,
            clickHint: '',
          },
          reviews: {
            list: [],
            showMore: false,
          },
        },
        columnistArticle: {
          articles: {
            list: [],
            showMore: false,
          },
          columnists: {
            list: [],
            showMore: false,
          },
        },
        joinColumnistUrl: '',
        render: {
          pageTitle: '少年商学院国际教育专栏',
          tabBar: [{
            type: 'activity',
            name: '圆桌活动',
          }, {
            type: 'columnistArticle',
            name: '作者文章',
          }],
        },
      };
    },
    computed: {
      noticeActivityMoreRoute() {
        return this.getBlockShowMoreRoute('noticeActivity');
      },
      reviewActivityMoreRoute() {
        return this.getBlockShowMoreRoute('reviewActivity');
      },
      selectedArticleMoreRoute() {
        return this.getBlockShowMoreRoute('selectedArticle');
      },
      columnistMoreRoute() {
        return this.getBlockShowMoreRoute('columnist');
      },
      showEmptyActivity() {
        const { loaded, activity } = this;
        const { notices, reviews } = activity;
        return loaded && !notices.list.length && !reviews.list.length;
      },
      showEmptyColumnistArticle() {
        const { loaded, columnistArticle } = this;
        const { articles, columnists } = columnistArticle;
        return loaded && !articles.list.length && !columnists.list.length;
      },
    },
    methods: {
      getBlockShowMoreRoute(blockName) {
        const blockNameMap = {
          noticeActivity: this.activity.notices,
          reviewActivity: this.activity.reviews,
          selectedArticle: this.columnistArticle.articles,
          columnist: this.columnistArticle.columnists,
        };
        const { showMore } = blockNameMap[blockName];
        if (showMore) {
          let route;
          switch (blockName) {
            case 'noticeActivity':
            case 'reviewActivity': {
              route = {
                name: 'EducationColumnActivityList',
                params: { type: blockName.replace('Activity', '') },
              };
              break;
            }
            default: {
              route = { name: `EducationColumn${upperCaseFirst(blockName)}List` };
            }
          }
          return route;
        }
        return null;
      },
      async fetchData() {
        this.loaded = false;
        const data = await getEducationColumnIndex();
        this.banner = data.banner;
        this.activity = data.activity;
        this.columnistArticle = data.columnistArticle;
        this.joinColumnistUrl = data.joinColumnistUrl;
        this.render = data.render;
        this.loaded = true;
      },
      handleClickJoinColumnist() {
        const { joinColumnistUrl } = this;
        locationHref(joinColumnistUrl);
      },
      handleClickBanner(item) {
        this.$router.push(item.url);
      },
    },
    created() {
      this.fetchData();
    },
  };
</script>

<style scoped lang="less">
  @import '~@/assets/styles/var.less';

  .page-education-column {
    &-banner {
      padding: @size-base;
      background-color: @color-white;

      &-content {
        padding: 0;
        margin: 0;
      }
    }

    &-tab-content {
      padding-bottom: @size-large;
    }

    &-header-block {
      margin-bottom: 0;
    }

    &-columnist-list {
      padding-top: 0;
    }

    &-empty {
      background: transparent;
      height: auto;

      /deep/ .header {
        padding-top: @size-large * 2;
      }
    }

    &-join-bar {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      font-size: @font-size-large;
      color: @color-white;
      background: @gradient-primary;
    }
  }
</style>
